import React, { Fragment, useState, useEffect } from 'react'
import { Box, Button, Checkbox, Dialog, DialogContent, DialogTitle, Grid, IconButton, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, Typography } from '@mui/material';
import { Email, KeyboardArrowLeft, KeyboardArrowRight, Send } from '@mui/icons-material';
import Colors from '../../Assets/Styles/Colors';
import BreadCrumbs from '../../Components/BreadCrumbs/BreadCrumbs';
import { CircleLoading } from '../../Components/Loaders/CircleLoading';
import { useNavigate } from 'react-router-dom';
import EventServices from '../../Api/Events/EventServices';
import { PrimaryButton } from '../../Components/Buttons';
import { OutlineInputField } from '../../Components/Fields/TextFields';
import { useForm } from 'react-hook-form';
import { ErrorToaster, SuccessToaster } from '../../Components/Toaster/Toaster';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';

const tableHead = [
  "Name",
  "Email",
  "Phone",
  "Company",
];

const columnsWithSelect = [
  "Select", ...tableHead,
];

function TablePaginationActions(props) {
  const { count, page, rowsPerPage, onPageChange } = props;

  const handleBackButtonClick = (event) => {
    onPageChange(event, page - 1);
  };

  const handleNextButtonClick = (event) => {
    onPageChange(event, page + 1);
  };

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, []);

  return (
    <Box sx={{ flexShrink: 0, ml: 2.5 }}>
      <IconButton
        onClick={handleBackButtonClick}
        disabled={page === 0}
        aria-label="previous page"
      >
        <KeyboardArrowLeft />
      </IconButton>
      <IconButton
        onClick={handleNextButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="next page"
      >
        <KeyboardArrowRight />
      </IconButton>
    </Box>
  );
};

function EmailDialog({ open, handleClose, selectedEmails }) {
  const { register, handleSubmit, formState: { errors }, reset } = useForm();
  const [loading, setLoading] = useState(false);
  const [ckValue, setCkValue] = useState("");

  const sendEmail = async (formData) => {
    setLoading(true);
    const obj = {
      usersEmail: Array.from(selectedEmails),
      subject: formData.subject,
      message: ckValue,
    }
    try {
      const result = await EventServices.sendEmail(obj);
      if (result.responseCode == 200) {
        SuccessToaster(result.message);
        reset();
        handleClose()
      }
    } catch (error) {
      ErrorToaster(error);
    } finally {
      setLoading(false);
    }
  };

  const handleEditorChange = (event, editor) => {
    setCkValue(editor.getData());
  };

  return (
    <Dialog
      open={open}
      fullWidth
      maxWidth={"lg"}
      onClose={handleClose}
    >
      <DialogTitle>Send Email</DialogTitle>
      <DialogContent sx={{ pt: `8px !important` }}>
        <Grid
          container
          spacing={2}
          component={"form"}
          onSubmit={handleSubmit(sendEmail)}
        >
          <Grid item md={12}>
            <OutlineInputField
              register={register("subject", {
                required: "Please enter subject"
              })}
              label={"Subject"}
              error={errors?.subject && true}
              helperText={errors?.subject?.message}
            />
          </Grid>
          <Grid item md={12}>
            <Box className="App">
              <CKEditor
                editor={ClassicEditor}
                config={{
                  toolbar: {
                    items: [
                      "undo",
                      "redo", "|",
                      "heading", "|",
                      "bold",
                      "italic",
                    ]
                  },
                }}
                data={ckValue}
                onBlur={(event, editor) => handleEditorChange(event, editor)}
                onChange={(event, editor) => {
                  const data = editor.getData();
                  setCkValue(data);
                }}
              />
            </Box>
          </Grid>
          <Grid item md={12}>
            <Grid container spacing={2}>
              <Grid item md={6}>
                <Button
                  fullWidth
                  variant={"outlined"}
                  sx={{
                    textTransform: "capitalize",
                    borderColor: Colors.primary,
                  }}
                  onClick={() => handleClose()}
                >
                  Cancel
                </Button>
              </Grid>
              <Grid item md={6}>
                <PrimaryButton
                  loading={loading}
                  disabled={loading}
                  fullWidth={true}
                  title={"Send"}
                  type={"submit"}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  )
}

function Users() {
  const [users, setUsers] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [count, setCount] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isSelectMode, setIsSelectMode] = useState(false);
  const [isSelectAll, setIsSelectAll] = useState(false);
  const [selectedEmails, setSelectedEmails] = useState(new Set()); // To track selected rows
  const [selectedEmailsPerPage, setSelectedEmailsPerPage] = useState(new Set());
  const [isEmailDialogOpen, setIsEmailDialogOpen] = useState(false);

  const navigate = useNavigate();

  const getUsers = async (page, limit) => {
    setLoading(true);
    try {
      const result = await EventServices.getEvents(page ? page : 1, limit ? limit : 10);
      if (result.responseCode == 200) {
        setUsers(result?.data?.userEvents);
        setCount(result.data.totalEvents);
        const newSelectedRowsPerPage = new Set(selectedEmailsPerPage);
        result?.data?.userEvents.forEach(row => {
          if (selectedEmails.has(row.email)) {
            newSelectedRowsPerPage.add(row.email);
          }
        });
        setSelectedEmailsPerPage(newSelectedRowsPerPage);
        const allSelected = result?.data?.userEvents.every(row => selectedEmails.has(row.email));
        setIsSelectAll(allSelected);
      }
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    getUsers(newPage + 1, rowsPerPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(Math.floor(event.target.value));
    setPage(0);
    getUsers(page, event.target.value);
  };

  const handleSelectAll = (isChecked) => {
    setIsSelectAll(isChecked);

    const newSelectedRows = new Set(selectedEmails);
    console.log("🚀 ~ handleSelectAll ~ newSelectedRows:", newSelectedRows)

    if (isChecked) {
      users.forEach(row => {
        newSelectedRows.add(row.email)
      })
      setSelectedEmailsPerPage(new Set(users.map(row => row.email)));
    } else {
      users.forEach(row => newSelectedRows.delete(row.email));
      setSelectedEmailsPerPage(new Set());
    }

    setSelectedEmails(newSelectedRows)
  };

  const handleCheckboxChange = (event, email) => {
    const isChecked = event.target.checked;
    const newSelectedRows = new Set(selectedEmails);

    if (isChecked) {
      newSelectedRows.add(email);
    } else {
      newSelectedRows.delete(email);
    }

    setSelectedEmails(newSelectedRows);

    // Update selectedEmailsPerPage
    const newSelectedRowsPerPage = new Set(selectedEmailsPerPage);
    if (isChecked) {
      newSelectedRowsPerPage.add(email);
    } else {
      newSelectedRowsPerPage.delete(email);
    }
    setSelectedEmailsPerPage(newSelectedRowsPerPage);

    // Update isSelectAll based on the current page's rows
    setIsSelectAll(users.every(row => newSelectedRowsPerPage.has(row.email)));
  };

  const handleEmailDialog = () => {
    setIsEmailDialogOpen((prevOpen) => !prevOpen);
  }

  useEffect(() => {
    getUsers();
  }, []);

  return (
    <Fragment>
      <EmailDialog open={isEmailDialogOpen} handleClose={handleEmailDialog} selectedEmails={selectedEmails} />
      <Box
        sx={{
          borderRadius: "12px",
          display: "flex",
          flexDirection: "column",
          gap: 2
        }}
      >
        <Grid
          container
          alignItems="center"
          justifyContent="space-between"
          sx={{
            background: Colors.primaryGradient,
            p: 2,
            borderRadius: "12px",
          }}
        >
          <Grid item xs={6}>
            <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <IconButton onClick={() => navigate(-1)}>
                <KeyboardArrowLeft sx={{ color: Colors.white }} />
              </IconButton>
              <Typography variant="body1" fontWeight="bold" sx={{ color: Colors.white }}>
                Users
              </Typography>
            </Box>
          </Grid>
          <Grid
            item
            xs={6}
            sx={{ display: "flex", justifyContent: "flex-end" }}
          >
            <BreadCrumbs currentPage={["Users"]} />
          </Grid>
        </Grid>
        <Grid container justifyContent={"space-between"}>
          <Grid size={3}>
            <PrimaryButton
              onClick={() => { setIsSelectMode(!isSelectMode); selectedEmails.clear(); setIsSelectAll((prevSelect) => !prevSelect) }}
              title={isSelectMode ? "Cancel" : "Select"}
            />
          </Grid>
          <Grid size={3}>
            {selectedEmails.size > 0 && (
              <PrimaryButton
                startIcon={<Send />}
                title={"Send Email"}
                onClick={() => handleEmailDialog()}
              />
            )}
          </Grid>
        </Grid>
        <Box>
          {loading ? (
            <CircleLoading />
          ) : (
            <Fragment>
              <TableContainer
                component={Paper}
                sx={{
                  // width: "calc(100vw - 320px)",
                  "&::-webkit-scrollbar": {
                    bgcolor: Colors.white,
                    height: "8px",
                    borderRadius: "10px",
                    cursor: "pointer",
                  },
                  "&::-webkit-scrollbar-thumb": {
                    bgcolor: Colors.primary,
                    borderRadius: "10px",
                    cursor: "pointer",
                  },
                  "&.MuiPaper-root": {
                    borderRadius: "12px",
                    boxShadow: 0,
                  },
                }}
              >
                <Table sx={{ minWidth: 650 }} aria-label="simple table">
                  <TableHead>
                    <TableRow sx={{ bgcolor: Colors.lightGray }}>
                      {(isSelectMode ? columnsWithSelect : tableHead).map((item, index) => (
                        <TableCell key={index}>
                          {item == "Select" ? (
                            <Checkbox
                              sx={{
                                p: 0,
                                color: `${Colors.primary} !important`
                              }}
                              checked={isSelectAll}
                              onChange={(e) => handleSelectAll(e.target.checked)}
                            />
                          ) : (
                            <Typography
                              sx={{
                                fontWeight: "bold",
                                // textAlign: item == "Posted By" ? "center" : item == "Actions" && "end",
                                width: "130px",
                              }}
                            >
                              {item}
                            </Typography>
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {users.map((item, colInd) => {
                      if (isSelectMode) {
                        return (
                          <TableRow key={item?._id}>
                            <TableCell
                              key={colInd}
                              sx={{
                                color: Colors.white,
                                p: "8px 16px"
                              }}
                            >
                              <Checkbox
                                sx={{
                                  p: 0,
                                  color: `${Colors.primary} !important`,
                                }}
                                checked={selectedEmails.has(item.email)}
                                onChange={(e) => handleCheckboxChange(e, item.email)}
                              />
                            </TableCell>
                            <TableCell>{item?.name || "-"}</TableCell>
                            <TableCell>{item?.email || "-"}</TableCell>
                            <TableCell>{item?.phoneNumber || "-"}</TableCell>
                            <TableCell>{item?.companyName || "-"}</TableCell>
                          </TableRow>
                        )
                      } else {
                        return (
                          <TableRow key={item?._id}>
                            <TableCell>{item?.name || "-"}</TableCell>
                            <TableCell>{item?.email || "-"}</TableCell>
                            <TableCell>{item?.phoneNumber || "-"}</TableCell>
                            <TableCell>{item?.companyName || "-"}</TableCell>
                          </TableRow>
                        )
                      }
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              <Table sx={{ marginTop: "12px" }}>
                <TablePagination
                  sx={{
                    borderBottom: "none",
                    bgcolor: Colors.white,
                    borderRadius: "12px",
                  }}
                  rowsPerPageOptions={[10, 20, 30]}
                  colSpan={12}
                  count={+count}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  SelectProps={{
                    inputProps: {
                      "aria-label": "rows per page",
                    },
                    native: true,
                  }}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  ActionsComponent={TablePaginationActions}
                />
              </Table>
            </Fragment>
          )}
        </Box>
      </Box>
    </Fragment>
  )
}

export default Users